import { Component, inject, OnInit, Input, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  BehaviorSubject,
  takeUntil,
  Subject,
} from 'rxjs';
import { CustomerFidelityService } from '../../../core/services/customer-fidelity.service';
import { lastValueFrom } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { IntegrationProfileService } from '../../../core/services/integration-profile.service';
import { ICustomer, IIntegration } from '../../../core/models/IIntegration';

interface DialogData {
  sourceCustomer?: ICustomer;
}

@Component({
  selector: 'app-clone-integration-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    AsyncPipe,
  ],
  templateUrl: './clone-integration-dialog.component.html',
})
export class CloneIntegrationDialogComponent implements OnInit {
  sourceCustomer!: ICustomer;
  targetCustomer!: ICustomer;
  form: FormGroup;
  filteredSourceOptions: Observable<ICustomer[]> | undefined;
  filteredTargetOptions: Observable<ICustomer[]> | undefined;
  filteredIntegrationOptions: Observable<IIntegration[]> | undefined;
  private integrationProfilesSubject = new BehaviorSubject<IIntegration[]>([]);
  integrationProfileService = inject(IntegrationProfileService);
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CloneIntegrationDialogComponent>,
    private customerFidelityService: CustomerFidelityService,
    @Inject(MAT_DIALOG_DATA) private data: DialogData
  ) {
    this.form = this.fb.group({
      sourceCustomer: [null, Validators.required],
      targetCustomer: [null, Validators.required],
      integrationProfile: [null, Validators.required],
    });
  }

  ngOnInit() {
    this.setupCustomerFilters();
    this.setupIntegrationFilter();

    if (this.data?.sourceCustomer) {
      this.initializeWithSourceCustomer(this.data.sourceCustomer);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.integrationProfilesSubject.complete();
  }

  private setupCustomerFilters() {
    const createCustomerFilter = (controlName: string) => {
      return this.form.get(controlName)?.valueChanges.pipe(
        debounceTime(100),
        startWith(''),
        distinctUntilChanged(),
        filter((value) => typeof value === 'string' && value.length >= 3),
        switchMap((value) => this.searchCustomer(value as string)),
        map((customers) => customers || []),
        takeUntil(this.destroy$) // Agregar takeUntil para limpiar la suscripción
      );
    };

    this.filteredSourceOptions = createCustomerFilter('sourceCustomer');
    this.filteredTargetOptions = createCustomerFilter('targetCustomer');
  }

  private setupIntegrationFilter() {
    this.filteredIntegrationOptions = this.form
      .get('integrationProfile')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const name = typeof value === 'string' ? value : value?.name;
          return name
            ? this._filterIntegration(name)
            : this.integrationProfilesSubject.value.slice();
        }),
        takeUntil(this.destroy$) // Agregar takeUntil para limpiar la suscripción
      );
  }

  private _filterIntegration(value: string): IIntegration[] {
    const filterValue = value.toLowerCase();
    return this.integrationProfilesSubject.value.filter((profile) =>
      profile.pms?.toLowerCase().includes(filterValue)
    );
  }

  async searchCustomer(query: string): Promise<ICustomer[]> {
    const customersFiltered$ = this.customerFidelityService.findByWord({
      query,
      type: 'client',
      show_privates: false,
    });
    return (await lastValueFrom(customersFiltered$)) as ICustomer[];
  }

  async searchIntegrationProfile(): Promise<IIntegration[]> {
    if (!this.sourceCustomer?.id) return [];
    const integrationProfileFiltered$ =
      this.integrationProfileService.getIntegrationProfileByCustomerId(
        this.sourceCustomer.id
      );
    const response = await lastValueFrom(integrationProfileFiltered$);
    const data = (response as any).data;
    if (Array.isArray(data)) {
      return data as IIntegration[];
    } else {
      return [data] as IIntegration[];
    }
  }

  displayFnCustomer(customer: ICustomer): string {
    return customer && customer.name ? `${customer.id} - ${customer.name}` : '';
  }

  displayFnIntegration(integration: IIntegration): string {
    return integration
      ? `${integration?.id || ''} - ${integration?.pms || ''} - ${
          integration?.type?.name || ''
        }`
      : '';
  }

  onSourceCustomerSelected(event: any) {
    this.sourceCustomer = event.option.value;
    this.form.patchValue({
      sourceCustomer: event.option.value,
      integrationProfile: null, // Reset integration profile when source changes
    });

    // Update integration profiles list
    this.searchIntegrationProfile().then((profiles) => {
      this.integrationProfilesSubject.next(profiles);
      if (profiles.length > 0) {
        this.form.patchValue({
          integrationProfile: profiles[0], // Set first profile as default
        });
      }
    });
  }

  onTargetCustomerSelected(event: any) {
    this.targetCustomer = event.option.value;
    this.form.patchValue({
      targetCustomer: event.option.value,
    });
  }

  async onSubmit() {
    if (this.form.valid) {
      const clonedIntegration = (
        (await lastValueFrom(
          this.integrationProfileService.cloneIntegrationProfile(
            this.form.value.integrationProfile.id,
            this.targetCustomer.id
          )
        )) as any
      ).data;
      this.dialogRef.close(clonedIntegration);
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  private async initializeWithSourceCustomer(
    customer: ICustomer
  ): Promise<void> {
    this.sourceCustomer = customer;
    this.form.patchValue({
      sourceCustomer: customer,
      integrationProfile: null,
    });

    const profiles = await this.searchIntegrationProfile();
    this.integrationProfilesSubject.next(profiles);

    if (profiles.length > 0) {
      this.form.patchValue({
        integrationProfile: profiles[0],
      });
    }
  }
}
